import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../../Sidebar/Sidebar";
import styles from "./Dashboard1.module.css";
import { url } from "../../App";
import Cookies from "js-cookie";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import BarChart from "./Components/BarChart";
import LineChart from "./Components/LineChart";
import { CategoryScale } from "chart.js";
import Chart from "chart.js/auto";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

// Tailwind + other analytics imports
import { getAnalyticsData } from "./services/geminiService";
import Header from "./Components/Header";
import DashboardMain from "./Components/Dashboard";
import DashboardSkeleton from "./Components/DashboardSkeleton";
import ErrorDisplay from "./Components/ErrorDisplay";
import { formatDateToYMD } from "./utils/formatters";
import "./dashboardTailwind.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

Chart.register(CategoryScale);

const getInitialFilters = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    dateRange: {
      startDate: formatDateToYMD(firstDayOfMonth),
      endDate: formatDateToYMD(today),
    },
    locations: [],
    salesPersons: [],
  };
};

const Dashboard = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [viewDashboard, setViewDashboard] = useState(null); // permission flag
  const navigate = useNavigate();
  const token = Cookies.get("Token");
  const email = Cookies.get("Email");

  // Analytics dashboard states
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [filters, setFilters] = useState(getInitialFilters);
  const isInitialLoad = useRef(true);

  const openView = (val) => setIsOpen(val);

  // Step 1: Fetch user permission
  useEffect(() => {
    if (email === "rupesh.bishnoi@credin.in") {
      setViewDashboard(true);
    } else {
      axios
        .post(url + "/user/getsingle", {
          email,
          token,
          useremail: email,
        })
        .then((res) => {
          if (res.data.logout === true) {
            toast.error(res.data.message);
            setTimeout(() => navigate("/"), 3000);
          } else {
            console.log(res.data);
            console.log(res.data[0].permission?.view_dashboard, 'permission')
            const permission = res.data[0].permission?.view_dashboard;
            setViewDashboard(!!permission);
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error(err.message);
          setViewDashboard(false);
        });
    }
  }, [email, navigate, token]);

  // Step 2: Fetch analytics data if user has permission
  const fetchData = useCallback(
    async (isBackgroundRefresh = false) => {
      if (!viewDashboard) return; // Only fetch if allowed
      if (!isBackgroundRefresh) setIsLoading(true);
      setError(null);
      try {
        const dashboardData = await getAnalyticsData(filters);
        setData(dashboardData);
        setLastUpdated(new Date());
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error"));
        console.error(err);
      } finally {
        if (isInitialLoad.current || !isBackgroundRefresh) {
          setIsLoading(false);
          isInitialLoad.current = false;
        }
      }
    },
    [filters, viewDashboard]
  );

  useEffect(() => {
    if (viewDashboard) fetchData(false);
  }, [fetchData, viewDashboard]);

  useEffect(() => {
    if (viewDashboard) {
      const intervalId = setInterval(() => fetchData(true), 6000000);
      return () => clearInterval(intervalId);
    }
  }, [fetchData, viewDashboard]);

  // Step 3: Conditional rendering
  if (viewDashboard === null) return <div>Loading dashboard...</div>;

  console.log("viewDashboard:", viewDashboard);

  return (
    <div className="flex min-h-screen bg-slate-100 font-sans overflow-auto">
      <Sidebar click={openView} dataSend={"dashboard"} />
      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-200 ${isOpen ? "ml-60 pl-3" : "ml-0 pl-0"
          }`}
      >

        {viewDashboard ? (
          // ========== ADVANCED DASHBOARD ==========
          <>
            <Header
              onFiltersApply={(newFilters) =>
                setFilters((prev) => ({ ...prev, ...newFilters }))
              }
              currentFilters={filters}
              availableLocations={data?.availableLocations ?? []}
              availableSalesPersons={data?.availableSalesPersons ?? []}
              lastUpdated={lastUpdated}
              isRefreshing={isRefreshing}
              onManualRefresh={async () => {
                setIsRefreshing(true);
                await fetchData(true);
                setIsRefreshing(false);
              }}
              error={error}
            />
            <main className="flex-1 overflow-x-hidden overflow-y-auto bg-slate-100 p-4 md:p-6 lg:p-8">
              {isLoading && <DashboardSkeleton />}
              {error && !isLoading && (
                <div className="flex items-center justify-center h-full">
                  <ErrorDisplay
                    error={error}
                    onRetry={async () => {
                      setIsRetrying(true);
                      await fetchData(false);
                      setIsRetrying(false);
                    }}
                    isRetrying={isRetrying}
                  />
                </div>
              )}
              {data && !isLoading && !error && <DashboardMain data={data} />}
            </main>
          </>
        ) : (
          // ========== SIMPLE DASHBOARD ==========
          <div className="main-container" style={{ marginTop: "4rem" }}>
            <div className="content-container">
              <h2 className="main-heading">Loan Management System</h2>
              <div className={styles.content}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "30%", marginRight: "1rem" }}>
                    <Card variant="outlined" sx={{ minWidth: 75 }}>
                      <CardContent>
                        <h3>Sales</h3>
                        <br />
                        <h4>100</h4>
                      </CardContent>
                    </Card>
                  </div>
                  <div style={{ width: "30%", marginRight: "1rem" }}>
                    <Card variant="outlined" sx={{ minWidth: 75 }}>
                      <CardContent>
                        <h3>Total Loans Disbursed</h3>
                        <br />
                        <h4>10000</h4>
                      </CardContent>
                    </Card>
                  </div>
                  <div style={{ width: "30%", marginRight: "1rem" }}>
                    <Card variant="outlined" sx={{ minWidth: 75 }}>
                      <CardContent>
                        <h3>Total Loans</h3>
                        <br />
                        <h4>56666</h4>
                      </CardContent>
                    </Card>
                  </div>
                  <div style={{ width: "30%" }}>
                    <Card variant="outlined" sx={{ minWidth: 75 }}>
                      <CardContent>
                        <h3>Total Leads</h3>
                        <br />
                        <h4>40000</h4>
                      </CardContent>
                    </Card>
                  </div>
                </div>
                <br />
                <BarChart />
                <br />
                <LineChart />
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
